import { Text } from '../../../../components/index.js'
import type { Product, ProductVariant } from '../../types/product'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { cn } from '../../../../helpers/index.js'

import ShippingMethod from './ShippingMethod.js'
import { ShippingPolicyModal } from './ShippingPolicyModal.js'
import type { DeliveryCustom, shippingMetafields, ShippingMethodType } from './type'
import { isWeightAvailable } from './utils.js'
import LearnMore from './LearnMore.js'
import { useBizProductContext } from '../../BizProductProvider.js'
import { withLayout } from '../../../../shared/Styles.js'

const PaidShipping = ({
  metafields,
  productList,
  onShippingInfoChange,
  variant,
  className,
}: {
  metafields: any
  productList: Array<Product & { variant: ProductVariant }>
  variant: ProductVariant
  onShippingInfoChange: (deliveryCustom: DeliveryCustom) => void
  className?: string
}) => {
  const { isLogin, openSignInPopup } = useBizProductContext()
  const [openShippingPolicyModal, setOpenShippingPolicyModal] = useState(false)
  const [init, setInit] = useState(false)

  const [selectedMethod, setSelectedMethod] = useState<ShippingMethodType | null>(null)

  const weight = useMemo(() => {
    return productList.reduce((acc, product) => acc + (product?.variant?.weight || 0), 0)
  }, [productList])

  const normalizedMethod = useCallback(
    (method: shippingMetafields, weight: number, index: number) => {
      return {
        id: `${method.__code}__${method.price}`,
        code: method.__code,
        mode: method.__mode,
        title: method.title,
        disabled: !isWeightAvailable(weight, method),
        price: method.price,
        subtitle: method.subtitle,
        tag: index !== 0 ? metafields?.memberOnly : '',
        error: !isWeightAvailable(weight, method) ? metafields.overWeightError : '',
      }
    },
    [metafields?.memberOnly, metafields.overWeightError]
  )

  const list = useMemo(() => {
    const normalizedList: ShippingMethodType[] = metafields?.list?.map((method: shippingMetafields, index: number) =>
      normalizedMethod(method, weight, index)
    )

    return normalizedList?.reduce((prev: ShippingMethodType[], method: ShippingMethodType) => {
      // 如果超重，同一个物流选项类型下，展示其中一个且不可选
      const sameTypeMethod = normalizedList.filter((m: ShippingMethodType) => m.code == method.code)
      let availableMethod: ShippingMethodType
      // 所有物流选项都不可选
      if (sameTypeMethod.every((m: ShippingMethodType) => m.disabled)) {
        availableMethod = sameTypeMethod[sameTypeMethod.length - 1]
      } else {
        // 可选物流选项
        availableMethod = sameTypeMethod.find((m: ShippingMethodType) => !m.disabled)!
      }
      // 当前物流选项已存在
      const hasSameMethod = prev.find((m: ShippingMethodType) => availableMethod.id === m.id)
      if (!hasSameMethod) {
        return [...prev, availableMethod]
      } else {
        return prev
      }
    }, [] as ShippingMethodType[])
  }, [normalizedMethod, metafields?.list, weight])

  const handleChangeShippingInfo = useCallback(
    (selectedMethod: ShippingMethodType) => {
      onShippingInfoChange({
        selected_delivery_code: selectedMethod.code,
        selected_delivery_mode: selectedMethod.mode,
      })
    },
    [onShippingInfoChange]
  )

  useEffect(() => {
    // 默认选中第一个物流选项
    if (metafields?.list?.length > 0 && !init) {
      const standardShippingMethod = normalizedMethod(metafields?.list[0], 0, 0)
      setSelectedMethod(standardShippingMethod)
      handleChangeShippingInfo(standardShippingMethod)
      setInit(true)
    }
  }, [normalizedMethod, metafields?.list, handleChangeShippingInfo, init])

  const toggleShipping = useCallback(
    (method: ShippingMethodType) => {
      if (method.disabled) {
        return
      }
      setSelectedMethod(method)
      handleChangeShippingInfo(method)
      // gaTrack({
      //   event: 'ga4Event',
      //   event_name: 'lp_button',
      //   member_active_status: isLogin ? 'active' : 'not active', //用于输入code激活会员弹'窗的按钮，区分会员激活状态，传active或not active
      //   event_parameters: {
      //     page_group: 'Product Detail Page' + variant.sku, //如果是首页，则传"Home Page";如果是listing的首屏按钮，传Product Detail Page+$SKU
      //     position: 'masterbanner1', //位置标记。首页首屏轮播位置传masterbanner1、masterbanner2、masterbanner3，第二屏传2nd_banner，第三屏传3rd_banner，或者取该模块小标题，或能识别出是哪个模块位置的文案，弹窗传pop;如果是购物车侧边栏，则传"Cart Pop Up"
      //     button_name: method.title, //取按钮文案，或能识别出是哪个按钮的文案,比如右上角关闭传close,社媒分享图标传对应平台名字如Facebook,图片切换则传对应展示图片的名字
      //     info: '', //默认空值，一般传复选框的文案内容，特殊情况可指定info值
      //   },
      // })
    },

    [isLogin, list, variant.sku, handleChangeShippingInfo, openSignInPopup]
  )

  return (
    <div
      id="ipc-product-paid-shipping"
      className={cn(
        ' laptop-md:mt-[96px] lg-desktop:mt-[128px] tablet:px-8 laptop:px-16 laptop-md:px-0 mt-16 px-4',
        className
      )}
    >
      <div className="flex flex-wrap justify-between space-x-2 md:space-x-1">
        <Text
          className="lg-desktop:text-[16px] text-[14px] font-bold leading-[1.2] text-[#1F2021]"
          html={metafields?.title}
        ></Text>
        {/* <button
          onClick={() => {
            setOpenShippingPolicyModal(true)
          }}
          className='text-brand-color text-[16px] font-semibold text-[#595959] underline md:text-[14px]'
        >
          {metafields?.policy}
        </button> */}
      </div>
      <div className="lg-desktop:mt-[16px] laptop-md:flex-nowrap mt-[12px] flex flex-wrap gap-[12px] overflow-hidden">
        {list.map((method: ShippingMethodType, index: number) => (
          <ShippingMethod
            key={index}
            index={index}
            item={method}
            active={selectedMethod?.code === method.code}
            toggleShipping={() => toggleShipping(method)}
            currencyCode={variant.price.currencyCode}
            metafields={metafields}
            className="laptop:w-[calc(50%-6px)] laptop-md:flex-1 w-full"
          />
        ))}
        {/* learn more 卡片 */}
        <LearnMore
          setOpenShippingPolicyModal={setOpenShippingPolicyModal}
          metafields={metafields}
          className="laptop:w-[calc(50%-6px)] laptop-md:w-[calc(33.33%-16px)] lg-desktop:w-[calc(33.33%-24px)] w-full"
        />
      </div>
      {list.find((method: ShippingMethodType) => method?.error) && (
        <Text
          className="laptop-md:mt-[16px] mt-[12px] font-bold leading-[1.4] text-[#D41435]"
          html={list.find((method: ShippingMethodType) => method.error)?.error as string}
          as="p"
        />
      )}

      {metafields?.seeAvailableArea && (
        <Text
          as="div"
          className="mt-[16px] font-bold leading-[1.4] text-[#080A0F] [&_a]:underline"
          html={metafields?.seeAvailableArea}
        />
      )}
      {metafields?.shippingPolicy && (
        <ShippingPolicyModal
          data={metafields?.shippingPolicy}
          onClose={() => setOpenShippingPolicyModal(false)}
          open={openShippingPolicyModal}
        />
      )}
    </div>
  )
}

export default withLayout(PaidShipping)
